/**
 * ============================================================================
 * AuthContext.js - Authentication Context for React
 * ============================================================================
 * Provides authentication state management using JWT tokens
 * Created: November 27, 2025 - Phase 2C Multi-Tenancy
 * 
 * FEATURES:
 * - JWT token storage in localStorage
 * - Auto-refresh tokens before expiration
 * - User and organization state management
 * - Login, logout, register functions
 * - Organization switching for multi-org users
 * ============================================================================
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// API URL configuration (same as App.js)
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// ============================================================================
// Organizers in more than one organization (Oct 10, 2026 — Dennis, organizer of
// his own group and co-organizer of Seacoast Church):
//  - Sign-in opens the organization he was last in ON THIS DEVICE (remembered per
//    sign-in email). Without one the server opens his first organization A–Z.
//  - Every token renewal says which organization the browser is on, so a renewal
//    can never move him into another one behind the header's back.
//  - The switcher's list is re-read from the server each time the dashboard opens,
//    so a newly added organization appears without signing out.
// ============================================================================
const lastOrgKey = (email) => `tto_last_org:${String(email || '').trim().toLowerCase()}`;
function rememberOrg(email, orgId) {
  try { if (email && orgId) localStorage.setItem(lastOrgKey(email), String(orgId)); } catch (e) { /* storage blocked */ }
}
function rememberedOrg(email) {
  try { return email ? localStorage.getItem(lastOrgKey(email)) : null; } catch (e) { return null; }
}
function currentOrgId() {
  try { const o = JSON.parse(localStorage.getItem('organization') || 'null'); return o && o.id ? o.id : null; } catch (e) { return null; }
}

// Token expiration buffer (refresh 5 minutes before expiry)
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes in milliseconds

// Create the context
const AuthContext = createContext(null);

/**
 * Parse JWT token to extract payload
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Check if token is expired or about to expire
 */
function isTokenExpired(token, bufferMs = 0) {
  if (!token) return true;
  
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  
  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  return Date.now() >= (expiresAt - bufferMs);
}

/**
 * AuthProvider Component
 * Wraps the app and provides authentication state
 */
export function AuthProvider({ children }) {
  // Authentication state
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Clear all auth state and localStorage
   */
  const clearAuth = useCallback(() => {
    setUser(null);
    setOrganization(null);
    setOrganizations([]);
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    localStorage.removeItem('organizations');
    
    console.log('🔒 Auth state cleared');
  }, []);

  /**
   * Store auth state in localStorage and state
   */
  const setAuthState = useCallback((data) => {
    const { user: userData, organization: orgData, organizations: orgsData, accessToken: access, refreshToken: refresh } = data;
    
    // Update state
    setUser(userData);
    setOrganization(orgData);
    setOrganizations(orgsData || []);
    setAccessToken(access);
    setRefreshToken(refresh);
    setIsAuthenticated(true);
    
    // Persist to localStorage
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('organization', JSON.stringify(orgData));
    if (orgsData) {
      localStorage.setItem('organizations', JSON.stringify(orgsData));
    }
    
    console.log('✅ Auth state updated:', userData?.email, '->', orgData?.name);
  }, []);

  /**
   * Refresh access token using refresh token
   */
  const refreshAccessToken = useCallback(async () => {
    const storedRefreshToken = refreshToken || localStorage.getItem('refreshToken');
    
    if (!storedRefreshToken) {
      console.log('⚠️ No refresh token available');
      clearAuth();
      return null;
    }
    
    try {
      console.log('🔄 Refreshing access token...');
      
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: storedRefreshToken, organizationId: currentOrgId() })
      });
      
      if (!response.ok) {
        console.log('❌ Token refresh failed');
        clearAuth();
        return null;
      }
      
      const data = await response.json();
      
      // Update tokens. The server sends no new refresh token (Oct 10, 2026: storing
      // the missing one wrote "undefined", and the NEXT renewal then signed him out).
      setAccessToken(data.accessToken);
      localStorage.setItem('accessToken', data.accessToken);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      // Update organization if provided
      if (data.organization) {
        setOrganization(data.organization);
        localStorage.setItem('organization', JSON.stringify(data.organization));
      }
      
      console.log('✅ Token refreshed successfully');
      return data.accessToken;
      
    } catch (error) {
      console.error('Token refresh error:', error);
      clearAuth();
      return null;
    }
  }, [refreshToken, clearAuth]);

  /**
   * Get valid access token (refresh if needed)
   * Checks sessionStorage first for impersonation tokens (tab-specific)
   */
  const getValidToken = useCallback(async () => {
    // First check sessionStorage for impersonation token (tab-specific)
    const impersonationToken = sessionStorage.getItem('impersonationToken');
    if (impersonationToken && !isTokenExpired(impersonationToken, TOKEN_REFRESH_BUFFER)) {
      return impersonationToken;
    }
    
    // Fall back to regular token
    const currentToken = accessToken || localStorage.getItem('accessToken');
    
    if (!currentToken) {
      return null;
    }
    
    // Check if token needs refresh
    if (isTokenExpired(currentToken, TOKEN_REFRESH_BUFFER)) {
      return await refreshAccessToken();
    }
    
    return currentToken;
  }, [accessToken, refreshAccessToken]);

  /**
   * Login with email and password
   */
  const login = async (email, password, organizationId = null) => {
    try {
      const post = (orgId) => fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies for session backward compatibility
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(), 
          password,
          organizationId: orgId 
        })
      });
      // The organization he was last in on this device, unless the caller named one.
      const remembered = organizationId ? null : rememberedOrg(email);
      let response = await post(organizationId || remembered);
      let data = await response.json();
      // Removed from the remembered organization since → sign in to his first one.
      if (!response.ok && remembered && response.status === 403) {
        response = await post(null);
        data = await response.json();
      }
      
      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Login failed' 
        };
      }
      
      // Check if org needs subscription setup BEFORE storing auth state
      if (data.organization?.subscriptionStatus === 'setup_pending' || data.requiresCheckout) {
        // Don't store in localStorage - org is not usable yet
        // LoginPage will put these in sessionStorage for Stripe checkout
        return { success: true, data, setupPending: true };
      }
      
      // Store auth state (only for fully activated orgs)
      setAuthState({
        user: data.user,
        organization: data.organization,
        organizations: data.organizations,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken
      });
      rememberOrg((data.user && data.user.email) || email, data.organization && data.organization.id);
      
      return { success: true, data };
      
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  /**
   * Register new user account
   */
  const register = async (name, email, password, organizationName = null) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: name.trim(),
          email: email.toLowerCase().trim(), 
          password,
          organizationName: organizationName?.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Registration failed' 
        };
      }
      
      return { success: true, data };
      
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
      
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken: currentRefreshToken })
      });
      
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  /**
   * Request password reset
   */
  const forgotPassword = async (email) => {
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Request failed' 
        };
      }
      
      return { success: true, message: data.message };
      
    } catch (error) {
      console.error('Forgot password error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  /**
   * Reset password with token
   */
  const resetPassword = async (token, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Password reset failed' 
        };
      }
      
      return { success: true, message: data.message };
      
    } catch (error) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  /**
   * Switch to a different organization
   */
  const switchOrganization = async (organizationId) => {
    try {
      const token = await getValidToken();
      
      const response = await fetch(`${API_URL}/auth/switch-organization`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ organizationId })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Failed to switch organization' 
        };
      }
      
      // Update state with new organization and token
      setOrganization(data.organization);
      setAccessToken(data.accessToken);
      localStorage.setItem('organization', JSON.stringify(data.organization));
      localStorage.setItem('accessToken', data.accessToken);
      
      rememberOrg(user && user.email, data.organization.id);
      console.log('✅ Switched to organization:', data.organization.name);
      return { success: true, organization: data.organization };
      
    } catch (error) {
      console.error('Switch organization error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  /**
   * Check authentication status on mount
   */
  useEffect(() => {
    // The switcher's list, fresh from the server (Oct 10, 2026). The list saved at
    // sign-in never changed afterwards, so an organizer added to a second
    // organization saw no switcher until he signed out and in again.
    const syncOrganizations = async (token) => {
      try {
        if (!token) return;
        const r = await fetch(`${API_URL}/auth/organizations`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (d && Array.isArray(d.organizations)) {
          setOrganizations(d.organizations);
          localStorage.setItem('organizations', JSON.stringify(d.organizations));
        }
      } catch (e) { /* keep the saved list */ }
    };

    const initAuth = async () => {
      console.log('🔍 Initializing auth state...');
      
      // ========================================================================
      // PHASE 2E FIX v2: Handle impersonation via URL parameter (tab-isolated)
      // This prevents impersonation from affecting other browser tabs
      // ========================================================================
      
      // Step 1: Check for impersonation data in URL (from Admin Dashboard)
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateParam = urlParams.get('impersonate');
      
      if (impersonateParam) {
        console.log('🔑 Impersonation data found in URL');
        try {
          // UTF-8-safe decode: must mirror the encoder in AdminDashboard.js
          // (btoa(unescape(encodeURIComponent(...)))). A plain atob() would
          // mangle non-Latin-1 characters in the org name (em dash "—", curly
          // quotes, accented names, emoji) into mojibake. escape+decodeURIComponent
          // reverses the encoder's byte-string step.
          const impersonationData = JSON.parse(
            decodeURIComponent(escape(atob(impersonateParam)))
          );
          
          // Store in sessionStorage (tab-specific, doesn't affect other tabs!)
          sessionStorage.setItem('impersonationToken', impersonationData.accessToken);
          sessionStorage.setItem('impersonationOrg', JSON.stringify(impersonationData.organization));
          
          // Clean up URL (remove impersonate parameter)
          urlParams.delete('impersonate');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
          
          console.log('✅ Impersonation data stored in sessionStorage, URL cleaned');
        } catch (err) {
          console.error('Failed to parse impersonation data:', err);
        }
      }
      
      // Step 2: Check for existing impersonation in sessionStorage (tab-specific)
      const sessionToken = sessionStorage.getItem('impersonationToken');
      const sessionOrg = sessionStorage.getItem('impersonationOrg');
      
      if (sessionToken && sessionOrg) {
        console.log('🔑 Using impersonation token from sessionStorage');
        
        if (!isTokenExpired(sessionToken, TOKEN_REFRESH_BUFFER)) {
          const payload = parseJwt(sessionToken);
          if (payload) {
            setAccessToken(sessionToken);
            setUser({
              id: payload.userId,
              email: payload.email,
              name: payload.name,
              role: payload.role
            });
            setOrganization({
              id: payload.organizationId,
              name: payload.organizationName,
              slug: payload.organizationSlug
            });
            setIsAuthenticated(true);
            setIsLoading(false);
            console.log('✅ Impersonation auth restored:', payload.organizationName);
            refreshOrganization();   // the token carries the name from when Access was clicked
            return;
          }
        } else {
          console.log('⚠️ Impersonation token expired, clearing sessionStorage...');
          sessionStorage.removeItem('impersonationToken');
          sessionStorage.removeItem('impersonationOrg');
        }
      }
      
      // ========================================================================
      // LEGACY: Handle old impersonation via localStorage (for backward compat)
      // This will be removed after confirming the new flow works
      // ========================================================================
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');
      const storedOrg = localStorage.getItem('organization');
      const storedOrgs = localStorage.getItem('organizations');
      const impersonatingOrg = localStorage.getItem('impersonatingOrg');
      
      // Clean up old impersonation data from localStorage to prevent future issues
      if (impersonatingOrg) {
        console.log('⚠️ Found old impersonation data in localStorage, cleaning up...');
        localStorage.removeItem('impersonatingOrg');
        // Don't remove accessToken here as it might be the user's real token
      }
      // ========================================================================
      // END LEGACY HANDLING
      // ========================================================================
      
      if (!storedAccessToken || !storedRefreshToken) {
        console.log('ℹ️ No stored tokens found');
        setIsLoading(false);
        return;
      }
      
      // Check if access token is still valid
      if (!isTokenExpired(storedAccessToken, TOKEN_REFRESH_BUFFER)) {
        // Token is still valid, restore state
        console.log('✅ Restored auth from localStorage');
        setAccessToken(storedAccessToken);
        setRefreshToken(storedRefreshToken);
        setUser(storedUser ? JSON.parse(storedUser) : null);
        setOrganization(storedOrg ? JSON.parse(storedOrg) : null);
        setOrganizations(storedOrgs ? JSON.parse(storedOrgs) : []);
        setIsAuthenticated(true);
        setIsLoading(false);
        syncOrganizations(storedAccessToken);   // not awaited: the dashboard opens at once
        refreshOrganization();                  // a rename since sign-in (Oct 10, 2026)
        return;
      }
      
      // Try to refresh the token
      console.log('🔄 Access token expired, attempting refresh...');
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Restore user data from localStorage after successful refresh
        setUser(storedUser ? JSON.parse(storedUser) : null);
        setOrganization(storedOrg ? JSON.parse(storedOrg) : null);
        setOrganizations(storedOrgs ? JSON.parse(storedOrgs) : []);
        setIsAuthenticated(true);
        syncOrganizations(newToken);
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, []); // Empty dependency array - run only on mount

  /**
   * Set up token refresh interval
   */
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    
    // Calculate time until token expires
    const payload = parseJwt(accessToken);
    if (!payload || !payload.exp) return;
    
    const expiresAt = payload.exp * 1000;
    const refreshTime = expiresAt - TOKEN_REFRESH_BUFFER - Date.now();
    
    if (refreshTime <= 0) {
      // Token already needs refresh
      refreshAccessToken();
      return;
    }
    
    console.log(`⏰ Token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
    
    const timeoutId = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
    
    return () => clearTimeout(timeoutId);
  }, [accessToken, isAuthenticated, refreshAccessToken]);

  /**
   * Refresh the organization name in auth state after settings save.
   * Fetches /api/settings and updates organization.name in state + localStorage
   * without requiring a full page reload.
   */
  const refreshOrganization = useCallback(async () => {
    try {
      // Oct 10, 2026: an admin 🔑 Access tab uses ITS token and keeps the name in its
      // own sessionStorage. Before, this read the regular login's token — another org's
      // settings — and the Access tab kept the name from the moment it was opened.
      const imp = sessionStorage.getItem('impersonationToken');
      const impersonating = !!(imp && !isTokenExpired(imp, TOKEN_REFRESH_BUFFER));
      const token = impersonating ? imp : localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`${API_URL}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) return;

      const data = await response.json();
      const newName = data.data?.organization_name;
      if (!newName) return;

      // Update organization state and localStorage with the new name
      setOrganization(prev => {
        if (!prev) return prev;
        const updated = { ...prev, name: newName };
        if (impersonating) {
          try {
            const so = JSON.parse(sessionStorage.getItem('impersonationOrg') || 'null');
            if (so) sessionStorage.setItem('impersonationOrg', JSON.stringify({ ...so, name: newName }));
          } catch (e) { /* keep the stored copy */ }
        } else {
          localStorage.setItem('organization', JSON.stringify(updated));
        }
        return updated;
      });

      console.log('✅ Organization name refreshed in auth context:', newName);
    } catch (error) {
      console.error('refreshOrganization error:', error);
    }
  }, []);

  // Context value
  const value = {
    // State
    user,
    organization,
    organizations,
    accessToken,
    isLoading,
    isAuthenticated,
    
    // Actions
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    switchOrganization,
    refreshOrganization,
    getValidToken,
    
    // Helpers
    clearAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth Hook
 * Custom hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext;
